import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import PurchaseHeader from '../components/manualPurchase/PurchaseHeader';
import emptyPurchasesIcon from '../assets/icons/purchase-empty.svg';
import { PurchaseError } from '../components/manualPurchase/PurchaseSummary';
import { useAuth } from '../contexts/AuthContext';
import { ROUTES } from '../constants';
import service from '../services/manualPurchaseService';
import {
  readPurchaseIntent,
  formatPurchaseMoney,
  formatPurchaseTime,
} from '../utils/manualPurchase';

const PurchasesPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const requestedPage = Number(params.get('page') || 1);
  const page =
    Number.isInteger(requestedPage) &&
    requestedPage > 0 &&
    requestedPage <= 1000000
      ? requestedPage
      : 1;
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    service
      .getHistory({ page, size: 20 })
      .then(result => {
        if (active) setHistory(result);
      })
      .catch(err => {
        if (active) setError(err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [page, refresh]);
  let saved;
  try {
    saved = readPurchaseIntent(user.id);
  } catch (err) {
    /* Create page handles recovery errors. */
  }
  const move = next => setParams(next > 1 ? { page: String(next) } : {});
  const start = history?.data.length
    ? (history.page - 1) * history.size + 1
    : 0;
  const end = history?.data.length ? start + history.data.length - 1 : 0;
  return (
    <div className='purchase-page space-y-[30px]'>
      <PurchaseHeader
        action={
          <Link
            to={ROUTES.MANUAL_PURCHASE}
            className='inline-block w-[186px] text-center rounded-md bg-blue-600 py-[10px] text-base text-white hover:bg-blue-700'
          >
            Create Purchases
          </Link>
        }
      />
      {saved && !saved.id && (
        <div className='rounded-lg border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900'>
          <Link className='underline' to={ROUTES.MANUAL_PURCHASE}>
            Resume your pending purchase confirmation
          </Link>
        </div>
      )}
      <section
        aria-label='History Purchases'
        className='purchase-history purchase-card overflow-hidden rounded-[10px] bg-white'
      >
        <div className='px-5 py-[30px]'>
          <h2 className='text-2xl font-semibold'>History Purchases</h2>
          <p aria-live='polite' className='mt-[3px] text-base text-gray-500'>
            {loading
              ? 'Loading purchases…'
              : error
              ? 'Purchases could not be loaded.'
              : `Showing ${start} to ${end} of ${
                  history?.total || 0
                } purchases`}
          </p>
        </div>
        {error ? (
          <div className='px-5 pb-5 space-y-4'>
            <PurchaseError error={error} />
            <button
              type='button'
              className='text-blue-600 underline'
              onClick={() => setRefresh(value => value + 1)}
            >
              Reload purchases
            </button>
          </div>
        ) : (
          <>
            <div className='overflow-x-auto'>
              <table
                className='purchase-table w-full min-w-[1133px] table-fixed text-left text-sm'
                aria-busy={loading}
              >
                <colgroup>
                  {[181, 180, 132, 165, 90, 140, 140, 105].map(
                    (width, index) => (
                      <col
                        key={index}
                        style={{ width: `${(width / 1133) * 100}%` }}
                      />
                    )
                  )}
                </colgroup>
                <thead className='bg-gray-50 text-[#95979a]'>
                  <tr>
                    {[
                      'Creator',
                      'Item',
                      'Payment Date & Time',
                      'Reference',
                      'Quantity',
                      'Total Price',
                      'Admin',
                      'Action',
                    ].map(label => (
                      <th
                        key={label}
                        className={`px-3 py-3 h-16 font-normal uppercase ${
                          label === 'Payment Date & Time'
                            ? ''
                            : 'whitespace-nowrap'
                        } ${label === 'Total Price' ? 'text-right' : ''}`}
                      >
                        {label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className='text-slate-700'>
                  {!loading &&
                    history?.data.map(row => (
                      <tr key={row.id} className='purchase-history-row'>
                        <td className='px-3 py-[22px]'>
                          <div
                            className='max-w-[160px] truncate'
                            title={row.creator.name}
                          >
                            {row.creator.name}
                          </div>
                        </td>
                        <td className='px-3 py-[22px]'>
                          <div
                            className='max-w-[165px] truncate'
                            title={row.item.name}
                          >
                            {row.item.name}
                          </div>
                        </td>
                        <td className='px-3 py-3'>
                          {formatPurchaseTime(row.paymentAt)}
                        </td>
                        <td className='px-3 py-[22px]'>
                          <div className='truncate' title={row.reference}>
                            {row.reference}
                          </div>
                        </td>
                        <td className='px-3 py-[22px]'>{row.quantity}</td>
                        <td className='px-3 py-[22px] text-right whitespace-nowrap'>
                          {formatPurchaseMoney(row.total)}
                        </td>
                        <td className='px-3 py-[22px]'>
                          <div
                            className='truncate'
                            title={row.recordedBy?.name}
                          >
                            {row.recordedBy?.name || '—'}
                          </div>
                        </td>
                        <td className='px-3 py-[22px]'>
                          <Link
                            className='font-medium text-blue-600 hover:underline'
                            to={`${ROUTES.generatePath(
                              ROUTES.MANUAL_PURCHASE_DETAIL,
                              { id: row.id }
                            )}${location.search}`}
                          >
                            Detail
                          </Link>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {loading ? (
              <p role='status' className='py-16 text-center text-gray-500'>
                Loading purchases…
              </p>
            ) : !history?.data.length ? (
              <div className='pt-[50px] pb-[80px] text-center text-[#667085]'>
                <img
                  src={emptyPurchasesIcon}
                  alt=''
                  aria-hidden='true'
                  width={40}
                  height={40}
                  className='mx-auto mb-[6px] h-10 w-10'
                />
                <p>No purchases data available.</p>
              </div>
            ) : (
              <div className='h-7' />
            )}
            {!loading && history && (history.pageCount > 1 || page > 1) && (
              <nav
                aria-label='Purchase history pagination'
                className='flex flex-wrap items-center justify-end gap-4 border-t px-5 py-4 text-sm'
              >
                <button
                  type='button'
                  disabled={page <= 1}
                  onClick={() => move(Math.max(1, page - 1))}
                  className='rounded border px-3 py-2 disabled:opacity-40'
                >
                  Previous
                </button>
                <span>
                  Page {page} of {Math.max(1, history.pageCount)}
                </span>
                <button
                  type='button'
                  disabled={page >= history.pageCount}
                  onClick={() => move(page + 1)}
                  className='rounded border px-3 py-2 disabled:opacity-40'
                >
                  Next
                </button>
              </nav>
            )}
          </>
        )}
      </section>
      <Outlet />
    </div>
  );
};
export default PurchasesPage;
